import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product variant identifier (SKU or barcode).
 * This component listens for variant update events and updates the display accordingly.
 * It handles updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} skuContainer - The container element for the identifier
 * @property {HTMLElement} sku - The span element that displays the identifier text
 *
 * @extends {Component<Refs>}
 */
class ProductSkuComponent extends Component {
  requiredRefs = ['skuContainer', 'sku'];

  connectedCallback() {
    super.connectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.addEventListener(ThemeEvents.variantUpdate, this.updateIdentifier);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.removeEventListener(ThemeEvents.variantUpdate, this.updateIdentifier);
  }

  /**
   * Resolves the configured identifier value from a variant resource.
   * @param {Record<string, unknown>} resource - Variant data from VariantUpdateEvent
   * @returns {string}
   */
  #getIdentifierValue(resource) {
    const identifierField = this.dataset.identifierField || 'sku';

    if (identifierField === 'barcode') {
      return typeof resource.barcode === 'string' ? resource.barcode : '';
    }

    return typeof resource.sku === 'string' ? resource.sku : '';
  }

  /**
   * Updates the displayed identifier.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateIdentifier = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    }

    if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    if (event.detail.resource) {
      const identifierValue = this.#getIdentifierValue(event.detail.resource);

      if (identifierValue) {
        this.style.display = 'block';
        this.refs.sku.textContent = identifierValue;
      } else {
        this.style.display = 'none';
        this.refs.sku.textContent = '';
      }
    }
  };
}

if (!customElements.get('product-sku-component')) {
  customElements.define('product-sku-component', ProductSkuComponent);
}
